import React, { useState } from 'react';
import { ArrowUpRight, Waves, Disc3, Music4, Mic, Radio, FileMusic } from 'lucide-react';
import { Link } from 'react-router-dom';

interface AppCardData {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  techSpecs: string[];
  status: 'live' | 'coming-soon' | 'beta';
  route: string;
  gradient: string;
}

/*
 * Each app is thematically derived from a blog post on manmitsinghmusic.com.
 * The blog → app mapping is:
 *
 */

const apps: AppCardData[] = [
  {
    id: 'microtonal-synth',
    title: 'Microtonal Synth Lab',
    subtitle: 'Raga × Synthesis',
    description:
      'A browser-based monophonic synthesizer tuned to the 22-Shruti microtonal scale. Load any Raga, engage high-portamento "Meend" glide, and perform expressive melodic phrases that break free of the Western 12-tone grid — exactly as described in our Raga-to-Synth Pipeline blog.',
    icon: <Waves className="w-6 h-6" />,
    techSpecs: ['Web Audio API', 'Microtonal Scala', 'MPE Pitch Bend', 'Touch Strip Mapping'],
    status: 'coming-soon',
    route: '/microtonal-synth',
    gradient: 'from-violet-500/20 to-purple-600/10',

  },
  {
    id: 'hybrid-groove',
    title: 'Hybrid Groove Engine',
    subtitle: 'Tabla × Techno',
    description:
      'Layer Tabla Bols over a four-on-the-floor techno kick in real-time. Quantize the Bayan anchors to a strict 1/16th grid while leaving Dayan strokes slightly unquantized to retain the human "push-and-pull." Dial in swing, note repeat, and groove exactly like the Polyrhythmic Foundations workflow.',
    icon: <Disc3 className="w-6 h-6" />,
    techSpecs: ['AudioWorklet', 'Sub-ms Scheduling', 'Swing Control', 'Live MIDI In'],
    status: 'coming-soon',
    route: '/hybrid-groove',
    gradient: 'from-teal-500/20 to-emerald-600/10',

  },
  {
    id: 'vedic-drone',
    title: 'Vedic Drone Sculptor',
    subtitle: 'Granular Ambient',
    description:
      'Drop in a Tanpura or Sitar recording and shatter it into thousands of overlapping grains. Modulate grain position, density, and size with slow LFOs, then feed through shimmer reverb and high-feedback delays to create infinite, evolving "Vedic Drones" — the exact technique from our Granular Synthesis blog.',
    icon: <Music4 className="w-6 h-6" />,
    techSpecs: ['GrainPlayer Engine', 'Shimmer Reverb', 'LFO Modulation', 'Drag-and-Drop WAV'],
    status: 'coming-soon',
    route: '/vedic-drone',
    gradient: 'from-indigo-500/20 to-blue-600/10',

  },
  {
    id: 'taal-trainer',
    title: 'Taal Cycle Trainer',
    subtitle: 'Rhythmic Mastery',
    description:
      'A visual 16-beat Teental cycle trainer with Sam, Taali, and Khaali markers. Start at a "painfully slow" 40 BPM — just like the Understanding Teental blog prescribes — and only advance when every syllable is crisp. Supports Jhaptaal, Rupak, Ektal, and Dadra cycles.',
    icon: <Radio className="w-6 h-6" />,
    techSpecs: ['Web Audio Clock', '±0.1ms Accuracy', 'Visual Beat Map', 'Progressive Tempo'],
    status: 'coming-soon',
    route: '/taal-trainer',
    gradient: 'from-blue-500/20 to-sky-600/10',

  },
  {
    id: 'bol-dojo',
    title: 'Bol Dojo',
    subtitle: 'Stroke Academy',
    description:
      'An interactive training ground for learning the fundamental Tabla strokes. Hear, see waveforms, and practise Na, Tin, Dha, Dhin, Ge, and Ke with real-time pitch detection feedback. Structured from beginner to advanced — the digital companion to our Getting Started with Tabla guide.',
    icon: <Mic className="w-6 h-6" />,
    techSpecs: ['AnalyserNode FFT', 'Pitch Detection', 'Sample Playback', 'Progress Tracking'],
    status: 'coming-soon',
    route: '/bol-dojo',
    gradient: 'from-amber-500/20 to-orange-600/10',

  },
  {
    id: 'session-bridge',
    title: 'Session Bridge',
    subtitle: 'Dual-Zone Controller',
    description:
      'A floor-friendly remote controller for your DAW. Hit Record, control transport, trigger samples, and monitor levels — all from your cross-legged performance position without walking to the desk. The software half of the Hybrid Musician Studio Layout concept.',
    icon: <FileMusic className="w-6 h-6" />,
    techSpecs: ['WebMIDI API', 'Touch Optimized', 'OSC Protocol', 'Wireless LAN Control'],
    status: 'coming-soon',
    route: '/session-bridge',
    gradient: 'from-rose-500/20 to-pink-600/10',

  },
];

const statusConfig = {
  live: { label: 'Live', dotColor: 'bg-emerald-400', textColor: 'text-emerald-400' },
  beta: { label: 'Beta', dotColor: 'bg-amber-400', textColor: 'text-amber-400' },
  'coming-soon': { label: 'Coming Soon', dotColor: 'bg-neutral-500', textColor: 'text-neutral-500' },
};

const AppCard: React.FC<{ app: AppCardData; index: number }> = ({ app, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const status = statusConfig[app.status];

  return (
    <div
      className="glass-card group cursor-pointer p-6 flex flex-col h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Header Row */}
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-xl bg-gradient-to-br ${app.gradient} border border-white/[0.05]`}>
          <span className="text-gold">{app.icon}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${status.dotColor} ${app.status === 'live' ? 'animate-pulse' : ''}`} />
          <span className={`text-[10px] font-mono uppercase tracking-wider ${status.textColor}`}>
            {status.label}
          </span>
        </div>
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-white mb-0.5 group-hover:text-gold transition-colors duration-300">
        {app.title}
      </h3>
      <p className="text-xs font-mono text-neutral-500 uppercase tracking-wider mb-3">
        {app.subtitle}
      </p>

      {/* Description */}
      <p className="text-sm text-neutral-400 leading-relaxed mb-3 flex-grow">
        {app.description}
      </p>



      {/* Tech Specs — visible on hover */}
      <div
        className={`flex flex-wrap gap-1.5 mb-4 transition-all duration-300 ${
          isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        {app.techSpecs.map((spec) => (
          <span key={spec} className="tech-badge">
            {spec}
          </span>
        ))}
      </div>

      {/* Launch Button — visible on hover */}
      <div
        className={`transition-all duration-300 ${
          isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        <Link
          to={app.status === 'live' ? app.route : `/notify?app=${encodeURIComponent(app.title)}`}
          className="w-full py-2.5 rounded-lg bg-gold/10 border border-gold/20 text-gold text-sm font-medium flex items-center justify-center gap-2 hover:bg-gold/20 transition-colors duration-200"
        >
          {app.status === 'live' ? 'Launch App' : 'Notify Me'}
          <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};

const AppDirectory: React.FC = () => {
  return (
    <section id="apps" className="px-6 py-20">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="text-xs font-mono text-gold/60 tracking-[0.3em] uppercase mb-3 block">
            App Directory
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Your Practice Room, <span className="text-gold">Digitized</span>
          </h2>
          <p className="text-neutral-400 max-w-xl mx-auto text-sm leading-relaxed">
            Each tool is engineered as a standalone micro-app — purpose-built,
            zero-latency, and optimized for the demands of serious riyaz.
          </p>
        </div>

        {/* Card Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {apps.map((app, i) => (
            <AppCard key={app.id} app={app} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default AppDirectory;
